import {
  Paragraph,
  Heading,
  Badge,
  Button,
  Flex,
  Share2Icon,
  ExternalLinkIcon,
  IconButton,
  Tooltip,
} from '@buffer-mono/popcorn'

import {
  useAccount,
  useAccountTimezone,
} from '~publish/contexts/AccountContext'
import { getZonedNow, isMonday } from '~publish/helpers/temporal'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { logError } from '~publish/legacy/utils/logError'

import type { WeeklyPostingStreakData } from '../hooks'
import { getStreakTopPercentLabel } from '../streakPercentile'
import { StreakHeader } from './StreakHeader'
import styles from '../Streaks.module.css'

import type { JSX, Ref } from 'react'

interface StreakContent {
  heading?: string
  text: string
  textHasHtml?: boolean
  cta?: string
  badge?: {
    text: string
    color?: 'green' | 'yellow' | 'gray'
  }
  share?: boolean
}

/** Maps a state-badge colour to the class that overrides the Popcorn badge. */
const BADGE_COLOR_CLASS: Record<'green' | 'yellow' | 'gray', string> = {
  green: styles.badgeGreen,
  yellow: styles.badgeYellow,
  gray: styles.badgeGray,
}

const getStreakContent = (
  streak: WeeklyPostingStreakData['weeklyPostingStreak'] | undefined,
  startOfWeek?: string,
  isMondayToday = false,
): StreakContent | null => {
  if (!streak) return null

  const weekStart = startOfWeek ?? 'sunday'
  const weekStartDisplay =
    weekStart.charAt(0).toUpperCase() + weekStart.slice(1)

  switch (streak.userStreakState) {
    case 'NewStreakNoProgress':
      return {
        heading: 'Keep posting,\nbuild a streak!',
        text: 'Post each week to grow your streak! Miss a week and it resets.',
        cta: 'Schedule Post',
      }
    case 'NewStreakOnTrack':
      return {
        heading: 'Great job!',
        text: 'Your streak will begin once the post you scheduled is published.',
        badge: {
          text: 'On track',
          color: 'green',
        },
      }
    case 'NewStreakOnTrackAndAhead':
      return {
        heading: 'Look at you go!',
        text: "You're on track for next week, too. Keep up the great work.",
        badge: {
          text: 'Ahead',
          color: 'green',
        },
      }
    case 'ExistingStreakOnTrack':
      return {
        heading: `${streak.count} week streak`,
        badge: {
          text: 'On track',
          color: 'green',
        },
        text: 'Add to your streak by scheduling a post for next week.',
        share: true,
      }
    case 'ExistingStreakOnTrackAndAhead':
      return {
        heading: `${streak.count} week streak`,
        badge: {
          text: 'Ahead',
          color: 'green',
        },
        text: "Look at you go! You're on track for next week, too.",
        cta: 'Schedule Post',
        share: true,
      }
    case 'NewStreakHit':
      return {
        heading: "You've started a streak! 🎉",
        text: 'Schedule at least one post each week to keep your streak going.',
        cta: 'Schedule Post',
        share: true,
      }
    case 'NewStreakHitAndAhead':
      return {
        heading: "You've started a streak! 🎉",
        text: "Look at you go! You're on track for next week. ",
        badge: {
          text: 'Ahead',
          color: 'green',
        },
        share: true,
      }
    case 'ExistingStreakHit':
      return {
        heading: `${streak.count} week streak`,
        text: 'Great job! Schedule a post for next week to keep the streak going.',
        cta: 'Schedule Post',
        share: true,
      }
    case 'ExistingStreakHitAndAhead':
      return {
        heading: `${streak.count} week streak`,
        badge: {
          text: 'Ahead',
          color: 'green',
        },
        text: "Look at you go! You're on track for next week, too.",
        share: true,
      }
    case 'ExistingStreakAtRisk':
      return {
        heading: `${streak.count} week streak`,
        // Monday leads with the fresh week rather than the deadline. The
        // badge still reads "At risk" — only the body copy changes.
        text: isMondayToday
          ? `New week! Schedule a post to keep your streak going. <a href="https://publish.buffer.com/create/templates" rel="noreferrer">Browse Templates</a>`
          : `Schedule a post before <b>${weekStartDisplay}</b> to keep your streak alive! <a href="https://publish.buffer.com/create/templates" rel="noreferrer">These templates can help.</a>`,
        textHasHtml: true,
        badge: {
          text: 'At risk',
          color: 'yellow',
        },
        cta: 'Schedule Post',
      }
    case 'StreakLost':
      return {
        heading: `Start a new streak`,
        text: 'Stuck for ideas?<br/><a href="https://publish.buffer.com/create/templates" rel="noreferrer">These templates can help.</a>',
        textHasHtml: true,
        badge: {
          text: `Last streak: ${streak.previousCount}`,
          color: 'gray',
        },
        cta: 'Schedule Post',
      }
    case 'ChannelConnectionStreakCelebration':
      return {
        heading: `Just in: You're on a ${streak.count} week streak!`,
        text: 'We ran the numbers for all your channels, and your streak goes back!',
        badge: {
          text: 'On track',
          color: 'green',
        },
        share: true,
      }
    case 'PerfectMonthCelebration':
      return {
        heading: `A perfect month, way to go! 🎉`,
        text: 'Celebrate your posting consistency.',
        share: true,
      }
    default: {
      const error = new Error('Unexpected streak status')
      logError(error, { metaData: { streak } })
      return null
    }
  }
}

interface StreakWidgetContentProps {
  streak: NonNullable<WeeklyPostingStreakData['weeklyPostingStreak']>
  headingId?: string
  initialFocusRef?: Ref<HTMLButtonElement>
  onClose: () => void
}

export const StreakWidgetContent = ({
  streak,
  headingId,
  initialFocusRef,
  onClose,
}: StreakWidgetContentProps): JSX.Element | null => {
  const { account } = useAccount()
  const timezone = useAccountTimezone()
  const { createNewPostInComposer } = usePostComposer()
  const channelsLength = account?.currentOrganization?.channels?.length

  const content = getStreakContent(
    streak,
    account?.preferences?.startOfWeek ?? undefined,
    isMonday(getZonedNow(timezone)),
  )
  if (!content) return null

  const topPercentLabel = getStreakTopPercentLabel(streak.percentileRank)

  const handleCtaClick = (): void => {
    const cta = 'publish-sidebar-streakwidget-createPost-1'

    void createNewPostInComposer({
      cta,
    })
    onClose()
  }

  const handleShareClick = (): void => {
    const cta = 'publish-sidebar-streakwidget-share-1'
    // Select all channels if there are less than 3
    // Otherwise, select the first 3
    const channelId = channelsLength && channelsLength > 3 ? undefined : 'all'
    const imageUrl = `https://buffer-publish.s3.us-east-1.amazonaws.com/images/streaks/w${streak.count}-p.png`
    const text = `I've reached a ${streak.count}-week posting streak with Buffer!`
    const altText = `An image of a plant growing into a ${streak.count}-week posting streak with Buffer`

    void createNewPostInComposer({
      cta,
      channels: channelId,
      prefillPostData: {
        text,
        media: {
          picture: imageUrl,
          thumbnail: imageUrl,
          alt_text: altText,
          width: '4000',
          height: '4000',
          uploaded: 'true',
          progress: '100',
        },
      },
    })
    onClose()
  }

  return (
    <div className={styles.popoverContent}>
      <StreakHeader>
        <StreakHeader.Background streak={streak} />
        <StreakHeader.Content streak={streak} />
      </StreakHeader>

      <div className={styles.popoverTextContainer}>
        <div>
          <Heading
            id={headingId}
            align="center"
            size="md"
            as="h3"
            className={styles.popoverTitle}
          >
            {content.heading}
          </Heading>
          {(content.badge || topPercentLabel) && (
            <div className={styles.popoverBadgeRow}>
              {content.badge && (
                <Badge
                  size="small"
                  className={BADGE_COLOR_CLASS[content.badge.color ?? 'green']}
                >
                  {content.badge.text}
                </Badge>
              )}
              {topPercentLabel && (
                <Tooltip content="Compared to other Buffer users">
                  <Badge
                    size="small"
                    className={styles.badgePercent}
                    tabIndex={0}
                  >
                    {topPercentLabel}
                  </Badge>
                </Tooltip>
              )}
            </div>
          )}
        </div>
        <Paragraph color="subtle" size="sm" className={styles.popoverText}>
          {content.textHasHtml ? (
            <span dangerouslySetInnerHTML={{ __html: content.text }} />
          ) : (
            content.text
          )}
        </Paragraph>
      </div>
      <Flex
        align="center"
        justify="center"
        gap="xs"
        className={styles.popoverActions}
      >
        {content.cta && (
          <Button
            ref={initialFocusRef}
            variant="primary"
            size="medium"
            onClick={handleCtaClick}
          >
            {content.cta}
          </Button>
        )}
        {content.share && content.cta && (
          <IconButton
            label="Share your streak"
            variant="secondary"
            size="medium"
            onClick={handleShareClick}
          >
            <Share2Icon />
          </IconButton>
        )}
        {content.share && !content.cta && (
          <Button
            ref={initialFocusRef}
            variant="secondary"
            size="medium"
            onClick={handleShareClick}
          >
            <ExternalLinkIcon />
            Share Your Streak
          </Button>
        )}
      </Flex>
    </div>
  )
}
